import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import styles from '../CustomOfferDetailsPage.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { SimpleModal } from '../../../../components/Modal';
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import Api from 'api';
import { PartialOffer } from "utils/types";
import { exitReact } from 'utils';
import { Analytics } from 'utils/analytics';


interface DeclineOfferProps {
    partialOffer: PartialOffer;
    offerId: number
  }
  
  interface Button {
    text: string;
    onClick: () => void;
    primary: boolean;
  }
  
  interface ModalContent {
    title: string;
    content: string | React.ReactNode;
    buttons: Button[];
  }
  
  interface RejectData {
    reason: string;
    counterOffer?: number;
  }

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window.app as any;

const DeclineOffer: React.FC<DeclineOfferProps> = ({ partialOffer, offerId }) => {
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<ModalContent>({title: "", content: "", buttons:[]});
    const [currentOfferId, setCurrentOfferId] = useState<number>(offerId);

    const job = partialOffer?.job;
    
    useEffect(() => {
        setCurrentOfferId(offerId);
      }, [offerId]);    


      const declineButtonHandler = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setModalContent({
          title: 'Decline this Offer?',
          content: '',
          buttons: [
            { text: 'Decline', onClick: () => popupDeclineSurvey(currentOfferId), primary: true },
            { text: 'Cancel', onClick: () => setIsModalOpen(false), primary: false }
          ]
        });
        setIsModalOpen(true);
      }, [currentOfferId]);

      const popupDeclineSurvey = useCallback((offerId: number) => {
        setModalContent({
          title: 'Help us improve by giving a reason for declining:',
          content: '',
          buttons: [
            { text: 'Hourly rate above our budget', onClick: () => counterOfferJob(offerId, { reason: 'Hourly rate above our budget' }), primary: true },
            { text: 'Looking for more experience', onClick: () => declineJob(offerId, { reason: 'Looking for more experience' }), primary: true  },
            { text: 'None', onClick: () => declineJob(offerId, { reason: 'None' }), primary: true  }
          ]
        });
        setIsModalOpen(true);
      }, []);

    const createCounterOfferSelector = useCallback((rate: number) => {
        return (
            <div className="hourly-rate-wrapper">
                <p>Acceptable Rate</p>
                <select className="job-partial-details-counter-offer no-fastclick" name="counter-offer">
                    <option value="" disabled selected>Select Hourly Rate</option>
                    {[...Array(10)].map((_, i) => (
                        <option key={i} value={rate - i - 1}>${rate - i - 1}</option>
                    ))}
                </select>
                <div id="hourly-rate-error-msg">Please select your acceptable rate to submit a counter offer.</div>
            </div>
        );
    }, []);

    const decliningCustomOfferPopup = useCallback((text: string = '') => {
        setModalContent({
            title: 'Thank you!',
            content: text,
            buttons: [
                {
                    text: 'OK',
                    onClick: () => {
                        setIsModalOpen(false);
                        exitReact('home');
                    }, primary: true 
                }
            ]
        });
        setIsModalOpen(true);
    }, [job]);

    const declineJob = useCallback(async (offerId: number, rejectData: RejectData, text: string = "") => {
        try {
          await Api.rejectPartialOffer(offerId, rejectData);
          Analytics.track('decline offer', partialOffer);
          decliningCustomOfferPopup(text);  
        } catch (err) {
          setModalContent({
            title: 'Error',
            content: err instanceof Error ? `Error declining offer. ${err.message}` : 'Error declining offer. Please try again.',
            buttons: [{ text: 'OK', onClick: () => setIsModalOpen(false), primary: true }]
          });
          setIsModalOpen(true);
        }
      }, [decliningCustomOfferPopup, partialOffer]);

    const counterOfferJob = useCallback((offerId: number, rejectData: any) => {
        setModalContent({
            title: 'Counter Offer',
            content: createCounterOfferSelector(partialOffer?.hourlyRate || 0),
            buttons: [
                {
                    text: 'Submit Counter Offer',
                    onClick: () => {
                        const selectedRate = document.querySelector('.job-partial-details-counter-offer') as HTMLSelectElement;
                        if (selectedRate && selectedRate.value) {
                            declineJob(offerId, { ...rejectData, counterOffer: Number(selectedRate.value) }, 'Your counter offer has been sent.');
                        } else {
                            alert('Please select an acceptable rate.');
                        }
                    }, primary: true 
                },
                { text: 'Cancel', onClick: () => setIsModalOpen(false), primary: false  }
            ]
        });
        setIsModalOpen(true);
    }, [declineJob]);

    return (
        <>
          <StyledButton
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            size="small"
            id="continue-button"
            className={styles.declineButton}
            onClick={declineButtonHandler}
          >
            Decline
          </StyledButton>
          <SimpleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
            <h2>{modalContent?.title}</h2>
            <div>{modalContent?.content}</div>
            {modalContent?.buttons && modalContent.buttons.map((button, index) => (
              <StyledButton key={index} onClick={button.onClick} variant={button.primary ? "contained" : "outlined"}>
                {button.text}
              </StyledButton>
            ))}
          </SimpleModal>
        </>
    );
};

export default DeclineOffer;